import React, { useEffect, useMemo, useState } from 'react';
import { Download, Filter, RefreshCw, Calendar, User as UserIcon, CreditCard } from 'lucide-react';
import vendaService from '../services/vendaService';
import { paymentTypeService } from '../services/paymentTypeService';
import userService, { User } from '../services/userService';

type PaymentTypeOption = { id: string; nome: string };

interface PaymentDetailEntry {
  vendaId: string;
  data: string;
  cliente?: string;
  vendedor?: string;
  status?: string;
  forma: string;
  valor: number;
}

interface PaymentSummaryEntry {
  forma: string;
  quantidade: number;
  total: number;
}

function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function normalizeReportPayload(payload: any): any[] {
  const raw = payload?.data ?? payload;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.content)) return raw.content;
  if (raw && typeof raw === 'object') return [raw];
  return [];
}

const PaymentReportPage: React.FC = () => {
  const [dataInicial, setDataInicial] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return toISODate(d);
  });
  const [dataFinal, setDataFinal] = useState<string>(() => toISODate(new Date()));
  const [tiposPagamentoIds, setTiposPagamentoIds] = useState<string[]>([]);
  const [vendedorId, setVendedorId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeOption[]>([]);
  const [sellers, setSellers] = useState<User[]>([]);
  const [details, setDetails] = useState<PaymentDetailEntry[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const pt = await paymentTypeService.list();
        setPaymentTypes(pt.data.map((x) => ({ id: x.id, nome: x.nome })));
      } catch (e) {
        console.warn('Falha ao listar tipos de pagamento', e);
      }
      try {
        const res = await userService.getUsersByRole('VENDEDOR');
        const sellersData = Array.isArray(res.data)
          ? res.data
          : (Array.isArray((res as any)?.data?.content) ? (res as any).data.content : []);
        setSellers(sellersData as User[]);
      } catch (e) {
        console.warn('Falha ao listar vendedores', e);
      }
    })();
  }, []);

  const buscar = async () => {
    setLoading(true);
    try {
      const response = await vendaService.relatorioVendas({ dataInicial, dataFinal, tiposPagamentoIds, vendedorId: vendedorId || undefined, status: status || undefined });
      const list = normalizeReportPayload(response.data);
      const flattened: PaymentDetailEntry[] = [];
      for (const obj of list) {
        const v = obj?.venda ?? obj;
        const vendaId = String(
          v?.numeroVenda ?? v?.id ?? v?.codigo ?? v?.numero ?? v?.numeroCupom ?? v?.uuid ?? ''
        );
        const data = v?.dataVenda ?? v?.dataCriacao ?? v?.criadoEm ?? v?.createdAt ?? v?.data ?? '';
        const cliente = v?.clienteNome ?? v?.nomeCliente ?? v?.cliente?.nome ?? v?.cliente ?? undefined;
        const vendedor = v?.vendedorNome ?? v?.vendedor?.nome ?? v?.vendedor ?? undefined;
        const pagamentos = Array.isArray(obj?.pagamentos) ? obj.pagamentos : (Array.isArray(v?.pagamentos) ? v.pagamentos : []);
        const status = v?.status ?? obj?.status ?? undefined;
        for (const p of pagamentos) {
          const forma = String(p?.formaPagamento ?? p?.tipoPagamentoNome ?? p?.tipo ?? 'Pagamento');
          const valor = Number(p?.valor ?? 0);
          flattened.push({ vendaId, data, cliente, vendedor, status, forma, valor });
        }
      }
      setDetails(flattened);
    } catch (err) {
      console.error('Erro ao carregar relatório por pagamento', err);
      setDetails([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = useMemo<PaymentSummaryEntry[]>(() => {
    const map: Record<string, { quantidade: number; total: number }> = {};
    for (const d of details) {
      if (!map[d.forma]) map[d.forma] = { quantidade: 0, total: 0 };
      map[d.forma].quantidade += 1;
      map[d.forma].total += Number(d.valor || 0);
    }
    return Object.entries(map)
      .map(([forma, { quantidade, total }]) => ({ forma, quantidade, total }))
      .sort((a, b) => a.forma.localeCompare(b.forma));
  }, [details]);

  const exportarCSV = () => {
    const resumoHeader = ['Forma de pagamento', 'Quantidade', 'Total'];
    const resumoRows = summary.map((s) => [s.forma, String(s.quantidade), String(s.total).replace('.', ',')]);

    // Cabeçalho dos detalhes
    const detalhesHeader = ['Venda', 'Data', 'Cliente', 'Vendedor', 'Status', 'Forma', 'Valor'];

    // Agrupar detalhes por forma e ordenar grupos conforme o resumo
    const grouped: Record<string, PaymentDetailEntry[]> = {};
    for (const d of details) {
      const key = d.forma || 'Pagamento';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(d);
    }

    const order = summary.map((s) => s.forma);
    const detalhesRowsGrouped: string[][] = [];

    for (const forma of order) {
      const rows = grouped[forma] || [];
      // Ordenar dentro de cada forma por data e venda
      rows.sort((a, b) => {
        const da = new Date(a.data || 0).getTime();
        const db = new Date(b.data || 0).getTime();
        if (da !== db) return da - db;
        return String(a.vendaId || '').localeCompare(String(b.vendaId || ''));
      });

      for (const d of rows) {
        detalhesRowsGrouped.push([
          d.vendaId,
          d.data,
          d.cliente ?? '',
          d.vendedor ?? '',
          d.status ?? '',
          d.forma,
          String(Number(d.valor || 0)).replace('.', ','),
        ]);
      }

      const subtotal = rows.reduce((acc, r) => acc + Number(r.valor || 0), 0);
      // Linha de subtotal para a forma atual
      detalhesRowsGrouped.push(['', '', '', '', `Subtotal ${forma}`, String(subtotal).replace('.', ',')]);
      // Linha em branco para separação visual
      detalhesRowsGrouped.push(['', '', '', '', '', '']);
    }

    const csvLines = [
      ['Resumo por pagamento'],
      resumoHeader,
      ...resumoRows,
      [''],
      ['Detalhamento de pagamentos'],
      detalhesHeader,
      ...detalhesRowsGrouped,
    ]
      .map((cols) => cols.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob(["\ufeff" + csvLines], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio-pagamentos-${dataInicial}_a_${dataFinal}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">Relatório por pagamento</h1>
          <p className="text-gray-600 mt-1">Resumo por forma de pagamento, com detalhes e exportação</p>
        </div>
        <div className="flex gap-3">
          <button onClick={buscar} className="berry-button-secondary flex items-center gap-2" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Carregando...' : 'Atualizar'}
          </button>
          <button onClick={exportarCSV} className="berry-button-primary flex items-center gap-2" disabled={details.length === 0}>
            <Download className="w-4 h-4" />
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="berry-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Inicial</label>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <input type="date" className="berry-input w-full" value={dataInicial} onChange={(e) => setDataInicial(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Final</label>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <input type="date" className="berry-input w-full" value={dataFinal} onChange={(e) => setDataFinal(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Vendedor (opcional)</label>
            <div className="flex items-center gap-2">
              <UserIcon className="w-4 h-4 text-gray-500" />
              <select className="berry-input w-full" value={vendedorId} onChange={(e) => setVendedorId(e.target.value)}>
                <option value="">Todos</option>
                {sellers.map((s) => (
                  <option key={s.id} value={s.id}>{s.nome || s.username}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Formas de pagamento</label>
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-gray-500" />
              <select multiple className="berry-input w-full h-28" value={tiposPagamentoIds} onChange={(e) => {
                const options = Array.from(e.target.selectedOptions).map((o) => o.value);
                setTiposPagamentoIds(options);
              }}>
                {paymentTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>{pt.nome}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status da venda</label>
            <div className="flex items-center gap-2">
              <select className="berry-input w-full" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">--</option>
                <option value="PENDENTE">PENDENTE</option>
                <option value="FINALIZADA">FINALIZADA</option>
                <option value="CANCELADA">CANCELADA</option>
                <option value="EM_PROCESSAMENTO">EM_PROCESSAMENTO</option>
                <option value="ENTREGUE">ENTREGUE</option>
                <option value="PARCIALMENTE_ENTREGUE">PARCIALMENTE_ENTREGUE</option>
              </select>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button onClick={buscar} className="berry-button flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Aplicar filtros
          </button>
        </div>
      </div>

      {/* Resumo por pagamento */}
      <div className="berry-card p-0 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Forma de pagamento</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantidade</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {summary.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-gray-500" colSpan={3}>Nenhum pagamento encontrado para os filtros informados</td>
              </tr>
            )}
            {summary.map((s) => (
              <tr key={s.forma} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm text-gray-700">{s.forma}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{s.quantidade}</td>
                <td className="px-4 py-2 text-sm text-right font-medium">{`R$ ${s.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detalhamento de pagamentos */}
      <div className="berry-card p-0 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Venda</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendedor</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Forma</th>
          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {details.length === 0 && (
          <tr>
            <td className="px-4 py-6 text-center text-gray-500" colSpan={7}>Nenhum pagamento encontrado para os filtros informados</td>
          </tr>
        )}
        {details.map((d, idx) => (
          <tr key={`${d.vendaId}-${idx}`} className="hover:bg-gray-50">
            <td className="px-4 py-2 text-sm text-gray-700">{d.vendaId || '-'}</td>
            <td className="px-4 py-2 text-sm text-gray-700">{d.data ? new Date(d.data).toLocaleString('pt-BR') : '-'}</td>
            <td className="px-4 py-2 text-sm text-gray-700">{d.cliente ?? '-'}</td>
            <td className="px-4 py-2 text-sm text-gray-700">{d.vendedor ?? '-'}</td>
            <td className="px-4 py-2 text-sm text-gray-700">{d.status ?? '-'}</td>
            <td className="px-4 py-2 text-sm text-gray-700">{d.forma}</td>
            <td className="px-4 py-2 text-sm text-right font-medium">{`R$ ${Number(d.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}</td>
          </tr>
        ))}
      </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentReportPage;